// BM-H398 - s1_testator. Writes the testator's own register entry (name,
// date of birth, address) and the s1 answers the engine reads
// (`relationship_status`, `has_lpa_in_place`).

import { QuestionRow, SectionIntro, Select, TextInput, YesNo, sectionProps } from "./fields";
import PersonPicker from "./PersonPicker";
import S1EarlierWills from "./S1EarlierWills"; // BM-H3638
import LifetimeTransfers from "./LifetimeTransfers"; // BM-H3969

export const RELATIONSHIP_STATUS_OPTIONS = [
  { value: "single", label: "Single" },
  { value: "married", label: "Married" },
  { value: "civil_partnership", label: "In a civil partnership" },
  { value: "cohabiting", label: "In a relationship but not married" },
  { value: "divorced", label: "Divorced or dissolved" },
  { value: "widowed", label: "Widowed" },
  { value: "separated", label: "Separated but not divorced" },
];

export default function S1Testator({ section, setSection, payload, update, person }) {
  const { s, set, nk, setNk } = sectionProps(section, setSection);
  const status = s.relationship_status;
  const married = status === "married" || status === "civil_partnership" || status === "separated";

  function setPerson(patch) {
    update((d) => {
      const p = d.couple.people_register.find((x) => x.id === person.id);
      if (p) Object.assign(p, patch);
    });
    if (patch.full_legal_name !== undefined) set({ full_legal_name: patch.full_legal_name });
    if (patch.date_of_birth !== undefined) set({ date_of_birth: patch.date_of_birth });
    if (patch.address !== undefined) set({ address: patch.address });
  }

  return (
    <div data-testid="section-s1_testator">
      <SectionIntro title="About you">
        We start with you: your full legal name, your address, and enough personal detail to identify you unambiguously in the will.
      </SectionIntro>

      <QuestionRow qid="s1_testator.full_legal_name" label="Your full legal name">
        <TextInput testid="s1-full-legal-name" value={person?.full_legal_name} onChange={(v) => setPerson({ full_legal_name: v })} />
      </QuestionRow>
      <QuestionRow qid="s1_testator.other_names" label="Any other names you have been known by" hint="One per line, or leave blank.">
        <TextInput testid="s1-other-names" value={(s.other_names || []).join(", ")} onChange={(v) => set({ other_names: v.split(",").map((x) => x.trim()).filter(Boolean) })} />
      </QuestionRow>
      <QuestionRow qid="s1_testator.date_of_birth" label="Your date of birth">
        <TextInput testid="s1-dob" type="date" value={person?.date_of_birth} onChange={(v) => setPerson({ date_of_birth: v || null })} />
      </QuestionRow>
      <QuestionRow qid="s1_testator.address" label="Your current address">
        <div style={{ display: "grid", gap: 6 }}>
          <TextInput testid="s1-address-line1" placeholder="First line" value={s.address?.line1} onChange={(v) => setPerson({ address: { ...(s.address || {}), line1: v } })} />
          <TextInput testid="s1-address-town" placeholder="Town" value={s.address?.town} onChange={(v) => setPerson({ address: { ...(s.address || {}), town: v } })} />
          <TextInput testid="s1-address-postcode" placeholder="Postcode" value={s.address?.postcode} onChange={(v) => setPerson({ address: { ...(s.address || {}), postcode: v } })} />
          {/* BM-H3960 item 32: only if the address is outside the United Kingdom. Blank with a UK postcode prints no country. */}
          <TextInput testid="s1-address-country" placeholder="Country (only if outside the United Kingdom)" value={s.address?.country} onChange={(v) => setPerson({ address: { ...(s.address || {}), country: v } })} />
        </div>
      </QuestionRow>
      <QuestionRow qid="s1_testator.has_national_insurance_number" label="Do you have a National Insurance number?" evidence>
        <YesNo testid="s1-has-ni" value={s.has_national_insurance_number} onChange={(v) => set({ has_national_insurance_number: v })} />
      </QuestionRow>
      <QuestionRow qid="s1_testator.relationship_status" label="Your marital or civil partnership status">
        <Select testid="s1-relationship-status" value={status} options={RELATIONSHIP_STATUS_OPTIONS} onChange={(v) => set({ relationship_status: v })} />
      </QuestionRow>

      {married && (
        <>
          <PersonPicker
            payload={payload}
            update={update}
            qid="s1_testator.spouse_person_id"
            label="Your spouse or civil partner"
            value={s.spouse_person_id}
            exclude={[person?.id]}
            onChange={(pid) => set({ spouse_person_id: pid })}
            testid="s1-spouse"
            defaultRelationship="spouse"
          />
          <QuestionRow qid="s1_testator.marriage_date" label="Date of the marriage or civil partnership" notKnown={nk("marriage_date")} onNotKnown={(on) => setNk("marriage_date", on)}>
            <TextInput testid="s1-marriage-date" type="date" value={s.marriage_date} disabled={nk("marriage_date")} onChange={(v) => set({ marriage_date: v || null })} />
          </QuestionRow>
        </>
      )}
      {status === "divorced" && (
        <QuestionRow qid="s1_testator.divorce_final_date" label="Date the divorce or dissolution became final" notKnown={nk("divorce_final_date")} onNotKnown={(on) => setNk("divorce_final_date", on)}>
          <TextInput testid="s1-divorce-date" type="date" value={s.divorce_final_date} disabled={nk("divorce_final_date")} onChange={(v) => set({ divorce_final_date: v || null })} />
        </QuestionRow>
      )}
      <QuestionRow qid="s1_testator.pending_proceedings" label="Do you have any pending marriage, civil partnership or divorce proceedings?">
        <YesNo testid="s1-pending-proceedings" value={s.pending_proceedings} onChange={(v) => set({ pending_proceedings: v })} />
      </QuestionRow>
      <QuestionRow qid="s1_testator.has_lpa_in_place" label="Do you have a lasting power of attorney in place?" evidence notKnown={nk("has_lpa_in_place")} onNotKnown={(on) => setNk("has_lpa_in_place", on)}>
        <YesNo testid="s1-has-lpa" value={s.has_lpa_in_place} disabled={nk("has_lpa_in_place")} onChange={(v) => set({ has_lpa_in_place: v })} />
      </QuestionRow>
      <QuestionRow qid="s1_testator.has_overseas_assets" label="Do you own property or hold accounts outside the UK, or have you lived outside the UK for a significant period in the past twenty years?" notKnown={nk("has_overseas_assets")} onNotKnown={(on) => setNk("has_overseas_assets", on)}>
        <YesNo testid="s1-overseas-assets" value={s.has_overseas_assets} disabled={nk("has_overseas_assets")} onChange={(v) => set({ has_overseas_assets: v })} />
      </QuestionRow>
      {/* BM-H3969 (option B): asked of every client, whatever structure they choose */}
      <LifetimeTransfers s={s} set={set} />
      <S1EarlierWills section={s} set={set} />
    </div>
  );
}
